<script setup lang="ts">
/**
 * A field for free-text tags (keywords, labels): each tag is a chip, the text
 * input sits after them in NxTagField's chrome. `,` `;` and Enter turn the
 * typed text into a tag, pasted lists are split at `,` `;` and line breaks,
 * Backspace in the empty input removes the last tag, leaving the field keeps
 * what was typed. Tags are trimmed and never doubled (case-insensitive).
 *
 * `v-model` is the list of tags. For addresses use NxRecipientInput.
 */
import { computed, inject, nextTick, ref, useId } from 'vue';
import { useBrowserAutofillOffAttrs } from './browserAutofill';
import NxChip from '../Chip/NxChip.vue';
import NxTagField from './NxTagField.vue';
import { labelInjectionKey } from './internal';

// Plain text input: stays out of browser autofill in a provideBrowserAutofillOff() scope.
const autofillAttrs = useBrowserAutofillOffAttrs()('text', undefined);
const props = withDefaults(
  defineProps<{
    placeholder?: string;
    /** Placeholder once tags are chosen. */
    placeholderMore?: string;
    /** Id of the text input; generated (and tied to a surrounding label) when omitted. */
    id?: string;
    /** Accessible name when no visible label names the field. */
    ariaLabel?: string;
    disabled?: boolean;
    /** Tags shown, not editable. */
    readonly?: boolean;
    /** Most tags accepted; further input is ignored. */
    max?: number;
    /** Scroll cap of the chip area (em). */
    maxHeight?: string;
    /** Accessible name of a chip's ×. */
    removeLabel?: (tag: string) => string;
    emptyText?: string;
  }>(),
  {
    placeholder: undefined,
    placeholderMore: '',
    id: undefined,
    ariaLabel: undefined,
    disabled: false,
    readonly: false,
    max: undefined,
    maxHeight: '5.5em',
    removeLabel: (tag: string) => `${tag} entfernen`,
    emptyText: '—',
  }
);

const model = defineModel<string[]>({ required: true });

const emit = defineEmits<{
  add: [tags: string[]];
  remove: [tag: string];
}>();

const inputId = props.id ?? useId();
const labelInjection = inject(labelInjectionKey, null);
if (labelInjection && !labelInjection.for.value) labelInjection.setFor(inputId);

const rootRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const query = ref('');

const SEPARATOR = /[,;\n\r]/;

function addTags(tags: readonly string[]) {
  if (props.disabled || props.readonly) return;
  const next = [...model.value];
  const seen = new Set(next.map((tag) => tag.toLowerCase()));
  const added: string[] = [];
  for (const raw of tags) {
    const tag = raw.trim();
    if (!tag || seen.has(tag.toLowerCase())) continue;
    if (props.max !== undefined && next.length >= props.max) break;
    seen.add(tag.toLowerCase());
    next.push(tag);
    added.push(tag);
  }
  if (added.length === 0) return;
  model.value = next;
  emit('add', added);
}

function removeAt(index: number, refocus: boolean) {
  if (props.disabled || props.readonly) return;
  const tag = model.value[index];
  if (tag === undefined) return;
  model.value = model.value.filter((_, i) => i !== index);
  emit('remove', tag);
  if (refocus) void nextTick(() => inputRef.value?.focus());
}

/** Turn the typed text into tags. */
function commit() {
  const text = query.value;
  query.value = '';
  if (inputRef.value) inputRef.value.value = '';
  addTags(text.split(SEPARATOR));
}

function onInput(event: Event) {
  const input = event.target as HTMLInputElement;
  const parts = input.value.split(SEPARATOR);
  const rest = parts.pop() ?? '';
  if (parts.length > 0) addTags(parts);
  query.value = rest.trimStart();
  if (input.value !== query.value) input.value = query.value;
}

function onPaste(event: ClipboardEvent) {
  const pasted = event.clipboardData?.getData('text') ?? '';
  if (!SEPARATOR.test(pasted)) return;
  event.preventDefault();
  query.value = `${query.value}${pasted}`;
  commit();
}

function onKeydown(event: KeyboardEvent) {
  if (event.isComposing || event.keyCode === 229) return;
  if (event.key === 'Enter' && query.value.trim()) {
    event.preventDefault();
    commit();
  } else if (event.key === 'Backspace' && query.value === '' && model.value.length > 0) {
    event.preventDefault();
    removeAt(model.value.length - 1, false);
  }
}

function onFocusout(event: FocusEvent) {
  const next = event.relatedTarget;
  if (next instanceof Node && rootRef.value?.contains(next)) return;
  if (query.value.trim()) commit();
}

const inputPlaceholder = computed(() =>
  model.value.length > 0 ? props.placeholderMore : props.placeholder
);

defineExpose({ commit, focus: () => inputRef.value?.focus() });
</script>

<template>
  <div
    ref="rootRef"
    class="nx-tag-input"
    :class="{ 'is-disabled': disabled, 'is-readonly': readonly }"
    data-testid="nx-tag-input"
    @focusout="onFocusout"
  >
    <NxTagField :max-height="maxHeight" class="nx-tag-input-field">
      <ul v-if="model.length > 0" class="nx-tag-input-chips" role="list">
        <li v-for="(tag, index) in model" :key="tag" class="nx-tag-input-item">
          <NxChip
            size="xs"
            :static="readonly"
            :removable="!readonly"
            :disabled="disabled"
            :remove-label="removeLabel(tag)"
            @remove="removeAt(index, true)"
          >
            {{ tag }}
          </NxChip>
        </li>
      </ul>
      <input
        v-if="!readonly"
        :id="inputId"
        ref="inputRef"
        class="nx-tag-input-input"
        type="text"
        autocomplete="off"
        v-bind="autofillAttrs"
        :aria-label="ariaLabel"
        :placeholder="inputPlaceholder"
        :disabled="disabled"
        :value="query"
        @input="onInput"
        @paste="onPaste"
        @keydown="onKeydown"
      />
      <span v-else-if="model.length === 0" class="nx-tag-input-empty">{{ emptyText }}</span>
    </NxTagField>
  </div>
</template>

<style scoped>
/* `type="search"` only keeps browser autofill out (browserAutofill.ts): no
   search styling, no clear button. */
input[type='search'] {
  appearance: none;
}

input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-decoration {
  appearance: none;
  display: none;
}

.nx-tag-input {
  min-width: 0;
}
/* The chips flow inline with the input (list semantics stay via role="list"). */
.nx-tag-input-chips {
  display: contents;
  list-style: none;
  margin: 0;
  padding: 0;
  /* The global type reset sizes every bare <ul> at 16px medium. */
  font-size: inherit;
  font-weight: var(--nxt-font-weight-regular);
}
.nx-tag-input-item {
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
}
.nx-tag-input-input {
  flex: 1 1 calc(var(--nxt-size-unit) * 6);
  min-width: calc(var(--nxt-size-unit) * 4);
  padding: 0 var(--nxt-space-2xs);
  border: none;
  /* The field around it draws the focus ring (NxTagField :has(input:focus-visible)). */
  outline: none;
  background: transparent;
  font: inherit;
  line-height: var(--nxt-line-height-control);
  /* A record's value: the design's ink axis may colour it (I-2510). */
  color: var(--nxd-ink, var(--nxt-input-fg));
}
.nx-tag-input-input::placeholder {
  color: var(--nxt-input-placeholder);
}
.nx-tag-input-input:disabled {
  cursor: not-allowed;
  color: var(--nxt-input-fg-disabled);
}
.nx-tag-input.is-disabled .nx-tag-input-field {
  background: var(--nxt-input-bg-disabled);
  cursor: not-allowed;
}
.nx-tag-input.is-readonly .nx-tag-input-field {
  background: transparent;
}
.nx-tag-input-empty {
  padding-inline: var(--nxt-space-2xs);
  color: var(--nxt-fg-subtle);
}
</style>
